import React, { useCallback, useEffect, useMemo } from 'react';

/* Material UI */
import { createTheme, ThemeProvider } from '@mui/material';

/* Socket IO */
import socketIOClient, { io } from 'socket.io-client';

/* React Redux  */
import { useDispatch, useSelector } from 'react-redux';

/* React Router */
import { useLocation, useNavigate } from 'react-router-dom';

/* Toastify */
/* eslint-disable complexity */
import { ToastContainer } from 'react-toastify';

/* Project */
import './App.scss';
import defaultTheme from 'utils/theme';
import { getItemInStorage } from 'utils/functions';
import defaultSuccessToast from 'utils/toastify/defaultSuccessToast';
import environments from 'config/environments';
import defaultErrorToast from 'utils/toastify/defaultErrorToast';
import Router from './handlers/Router';
import { deleteObjectsReducer, getStateFromApi, mergeObjectsReducer } from './app.actions';
import {
  uploadReportInformationFail,
  updateReportData,
  fetchReportDetailsSuccess,
  fetchVehiclePrice,
} from './features/create-report/createReport.actions';
import {
  fetchReportSuccess,
  clearReportInformation,
  fetchReportFail,
} from './features/search-report/searchReport.actions';

function App({ route }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Redirect route for test environment
    if (route) navigate(route);
  }, [route]);

  const user = useSelector((state) => state.app.user);
  const loading = useSelector((state) => state.app.loading);

  useEffect(() => {
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: environments.GOOGLE_CLIENT_ID,
      });
    }

    dispatch(getStateFromApi());
  }, []);

  useEffect(() => {
    // This code is to add a confirm dialog for the user when tries to refresh the page while creating a new vehicle report
    // Users who use this App use mostly tablets, so refreshing the page unintentionally when they swipe up too hard may be an scenario
    if (
      location.pathname.split('/').slice(-1)[0] === 'edit' ||
      location.pathname.split('/').slice(-1)[0] === 'create'
    ) {
      window.onbeforeunload = () =>
        '¿Estás seguro que quieres recargar la página? La información ingresada hasta el momento se perderá';
    } else {
      window.onbeforeunload = () => {};
    }
  }, [location.pathname]);

  const authSocketPayload = useMemo(() => {
    const userToken = getItemInStorage('user')?.token;
    return { token: userToken };
  }, [user]);

  const webSocketInstance = useCallback(() => {
    const payload = { auth: { token: authSocketPayload.token } };
    return environments.WS_ENDPOINT
      ? socketIOClient(environments.WS_ENDPOINT, payload)
      : io(payload);
  }, [authSocketPayload]);

  const socket = useMemo(() => {
    if (user) return webSocketInstance();

    return null;
  }, [user]);

  useEffect(() => {
    if (socket) {
      socket.on('connect', () => {
        localStorage.setItem('socketId', socket.id);
      });

      // socket.on('exception', (exception) => {
      //   console.log('[Socket] Exception', { exception });
      // });
      //
      // socket.on('disconnect', () => {
      //   console.log('[Socket] Disconnected');
      // });

      socket.on('[Notification]', (data) => {
        defaultSuccessToast({ message: data.message });
      });

      socket.on('[MERGE_OBJECTS]', (data) => {
        dispatch(mergeObjectsReducer(data));
      });

      socket.on('[DELETE_OBJECTS]', (data) => {
        dispatch(deleteObjectsReducer(data));
      });

      socket.on('[Generate_and_send_PDF]', async (data) => {
        if (data?.error) {
          await defaultErrorToast({
            message: data.message,
          });
          await dispatch(uploadReportInformationFail());
        } else {
          await defaultSuccessToast({
            message: data.message,
          });
        }
      });

      socket.on('[QUEUE_RESPONSE]', (data) => {
        defaultSuccessToast({ message: 'A new action is register' });
        console.table({ data });
      });

      // Listener para cuando lleguen los datos de reportInfo desde SAP
      socket.on('[REPORT_INFO_LOADED]', (data) => {
        if (!data.error) {
          defaultSuccessToast({ message: '¡Fichas encontradas con éxito!' });
          dispatch(fetchReportSuccess(data));
        } else {
          defaultErrorToast({ message: data.error?.msg || 'Error al buscar fichas' });
          dispatch(clearReportInformation());
          dispatch(fetchReportFail());
        }
      });

      // Listener para errores en la consulta de reportInfo
      socket.on('[REPORT_INFO_ERROR]', (data) => {
        defaultErrorToast({ message: data.error?.msg || 'Error al consultar fichas en SAP' });
        dispatch(clearReportInformation());
        dispatch(fetchReportFail());
      });

      // Listener para cuando lleguen los detalles del reporte desde SAP
      // eslint-disable-next-line max-depth
      socket.on('[REPORT_DETAILS_LOADED]', async (data) => {
        if (data.success && data.requestId) {
          // Hacer fetch HTTP para obtener los datos completos
          try {
            const response = await fetch(
              `/api/v1/vehicle_reports/report_details/${data.requestId}`,
            );
            const fullData = await response.json();
            // eslint-disable-next-line max-depth
            if (!fullData.error) {
              defaultSuccessToast({ message: '¡Detalles de la ficha cargados con éxito!' });
              dispatch(
                updateReportData({
                  commentary: fullData.objects.sapReportInformation.vehicleInformation.comments,
                }),
              );
              dispatch(fetchReportDetailsSuccess(fullData));
              dispatch(
                fetchVehiclePrice({
                  vehicleInformation: {
                    vehicleYear:
                      fullData.objects.sapReportInformation.vehicleInformation.vehicleYear,
                    vehicleModel:
                      fullData.objects.sapReportInformation.vehicleInformation.vehicleModel,
                    vehicleMileage:
                      fullData.objects.sapReportInformation.vehicleInformation.vehicleMileage,
                    licensePlate:
                      fullData.objects.sapReportInformation.vehicleInformation.vehicleLicensePlate,
                  },
                  toggleNewMileageModal: false,
                }),
              );
            } else {
              defaultErrorToast({
                message: fullData.message || 'Error al obtener detalles de la ficha',
              });
              dispatch(fetchReportFail());
            }
          } catch (error) {
            defaultErrorToast({
              message: 'Error al obtener detalles de la ficha',
            });
            dispatch(fetchReportFail());
          }
        } else {
          defaultErrorToast({
            message: data.error?.msg || 'Error al obtener detalles de la ficha',
          });
          dispatch(fetchReportFail());
        }
      });

      // Listener para errores en la consulta de detalles del reporte
      socket.on('[REPORT_DETAILS_ERROR]', (data) => {
        defaultErrorToast({
          message: data.error?.msg || 'Error al consultar detalles de la ficha en SAP',
        });
        dispatch(fetchReportFail());
      });

      return () => {
        socket.disconnect();
      };
    }

    return () => {};
  }, [socket]);

  const theme = createTheme(defaultTheme);

  return (
    <ThemeProvider theme={theme}>
      <Router user={user} loading={loading} />
      <ToastContainer />
    </ThemeProvider>
  );
}

export default App;
